import { DesktopDateTimeRangePicker as DateTimeRangePicker } from '@mui/x-date-pickers-pro/DesktopDateTimeRangePicker';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import DateRangeIcon from '@mui/icons-material/DateRange';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { DateTimeRangeValidationError } from '@mui/x-date-pickers-pro';
import { palette } from 'theme/palette';
import { pxToRem } from 'src/common/utils/fonts';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';

type DateRangeTimeValuePiece = Date | null;
type DateRangeTimeValue = [DateRangeTimeValuePiece, DateRangeTimeValuePiece];

export type DateRangeTimePickerProps = {
  value: [DateRangeTimeValuePiece, DateRangeTimeValuePiece];
  onChange: (value: DateRangeTimeValue) => void;
  minDate?: Date;
  maxDate?: Date;
  onError?: (error: DateTimeRangeValidationError) => void;
  disableFuture?: boolean;
};

const BoxStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& .MuiMultiInputDateTimeRangeField-separator': {
    display: 'none',
  },
  '& .MuiTextField-root': {
    marginLeft: 0,
    '&:first-of-type': {
      '& .MuiInputBase-formControl': {
        maxWidth: 'fit-content',
        '& fieldset': {
          borderRadius: `${DEFAULT_BORDER_RADIUS} 0 0 ${DEFAULT_BORDER_RADIUS}`,
        },
      },
    },
    '&:last-of-type': {
      '& .MuiInputBase-formControl': {
        maxWidth: 'fit-content',
        '& fieldset': {
          borderRadius: `0 ${DEFAULT_BORDER_RADIUS} ${DEFAULT_BORDER_RADIUS} 0`,
        },
      },
    },
    '& .calendarIcon': {
      cursor: 'pointer',
    },
  },
  '& .MuiInputLabel-root': {
    opacity: '60%',
    top: theme.spacing(-6),
  },
  '& .MuiInputLabel-shrink': {
    opacity: '100%',
    top: '0',
    span: {
      fontSize: pxToRem(15),
      color: palette.text.secondary,
    },
  },
  '& label': {
    textTransform: 'capitalize',
  },
  '& .MuiInputBase-root.Mui-error, .MuiInputBase-root.Mui-error.Mui-focused fieldset': {
    borderColor: `${palette.red['400']} !important`,
    '&:hover': {
      fieldset: {
        borderColor: palette.red['400'],
      },
    },
  },
  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.red['400'],
    '&:hover': {
      fieldset: {
        borderColor: palette.red['400'],
      },
    },
  },
}));

export const DateRangeTimePicker: React.FC<DateRangeTimePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  onError,
  disableFuture = false,
}) => {
  const { t } = useTranslation();
  return (
    <BoxStyled data-testid="dateTimeRangePickerContainer">
      <DateTimeRangePicker
        // @ts-expect-error -- month, year aren't allowed in types
        views={['day', 'month', 'year', 'hours', 'minutes']}
        value={value}
        onChange={onChange}
        minDate={minDate ?? undefined}
        maxDate={maxDate ?? undefined}
        disableFuture={disableFuture}
        slotProps={{
          popper: {
            // @ts-expect-error -- data-testid works but MUI's types don't allow it
            'data-testid': 'calendar-tooltip',
          },
          desktopPaper: {
            // @ts-expect-error -- data-testid works but MUI's types don't allow it
            'data-testid': 'calendar',
          },
          calendarHeader: {
            // @ts-expect-error -- data-testid works but MUI's types don't allow it
            'data-testid': 'calendar header',
          },
          day: {
            // @ts-expect-error -- data-testid works but MUI's types don't allow it
            'data-testid': 'calendar day',
          },
          textField: ({ position }) => ({
            label:
              position === 'start' ? (
                <Typography variant="text1" color="text.primary" data-testid="fromLabel">
                  {t('common.from')}
                </Typography>
              ) : (
                <Typography variant="text1" color="text.primary" data-testid="toLabel">
                  {t('common.to')}
                </Typography>
              ),
            'data-testid': position === 'start' ? 'fromContainer' : 'toContainer',
            InputProps: {
              'data-testid': position === 'start' ? 'fromInput' : 'toInput',
              name: position === 'start' ? 'from' : 'to',
              endAdornment: (
                <DateRangeIcon
                  data-testid={position === 'start' ? 'fromIcon' : 'toIcon'}
                  className="calendarIcon"
                  sx={{ color: ({ palette }) => palette.text.secondary }}
                />
              ),
            },
          }),
        }}
        onError={onError}
      />
    </BoxStyled>
  );
};
